// Highlight manager for selection and hover visual feedback
// Creates appropriate highlight geometries for each object type

import * as THREE from 'three'
import type { Plant, Building, GroundShape, BoundaryObject, Zone, ContextPin } from '@/types'
import type { SelectableObject, SelectionResult } from './SelectionManager'
import { debug } from '@/utils/debug'
import { DPM_SCALE } from '@/constants/coordinates'

export interface HighlightConfig {
  selectionColor: number
  hoverColor: number
  lineWidth: number
  selectionOpacity: number
  hoverOpacity: number
  hoverDelay: number
}

const DEFAULT_CONFIG: HighlightConfig = {
  selectionColor: 0x2196F3,    // Material blue
  hoverColor: 0x64B5F6,        // Light blue
  lineWidth: 0.03,             // Tube radius in meters (for thick rings)
  selectionOpacity: 0.3,       // Semi-transparent fill
  hoverOpacity: 0.2,
  hoverDelay: 150              // ms before hover highlight appears
}

export class HighlightManager {
  private scene: THREE.Scene
  private highlightGroup: THREE.Group
  private config: HighlightConfig

  private selectionHighlight: THREE.Object3D | null = null
  private hoverHighlight: THREE.Object3D | null = null
  private hoverTimeout: ReturnType<typeof setTimeout> | null = null
  private pendingHoverObject: SelectableObject | null = null
  private multiSelectionHighlights: THREE.Object3D[] = []

  constructor(scene: THREE.Scene, config?: Partial<HighlightConfig>) {
    this.scene = scene
    this.config = { ...DEFAULT_CONFIG, ...config }

    // Create group for highlights (rendered on top)
    this.highlightGroup = new THREE.Group()
    this.highlightGroup.name = 'highlights'
    this.highlightGroup.renderOrder = 999
    this.scene.add(this.highlightGroup)

    debug.log('Highlight', 'HighlightManager initialized')
  }

  /**
   * Show selection highlight (immediate)
   * @param colorOverride — optional hex color to use instead of default selection color (e.g. for locked objects)
   */
  showSelection(result: SelectionResult, colorOverride?: number): void {
    this.clearSelection()

    const color = colorOverride ?? this.config.selectionColor
    const highlight = this.createHighlight(result, color, this.config.selectionOpacity)
    if (highlight) {
      this.selectionHighlight = highlight
      this.highlightGroup.add(highlight)
      debug.log('Highlight', `Selection highlight: ${result.object.type} ${result.object.id}`)
    }
  }

  /**
   * Update selection highlight (rebuild at current position).
   * Called during drag operations to keep the outline following the object.
   */
  updateSelection(result: SelectionResult, colorOverride?: number): void {
    this.showSelection(result, colorOverride)
  }

  /**
   * Show hover highlight (with delay)
   */
  showHover(result: SelectionResult, colorOverride?: number): void {
    // Cancel any pending hover
    this.cancelPendingHover()

    // Store the pending object
    this.pendingHoverObject = result.object

    // Set timeout for delayed highlight
    this.hoverTimeout = setTimeout(() => {
      if (this.pendingHoverObject === result.object) {
        this.clearHover()
        const color = colorOverride ?? this.config.hoverColor
        const highlight = this.createHighlight(result, color, this.config.hoverOpacity)
        if (highlight) {
          this.hoverHighlight = highlight
          this.highlightGroup.add(highlight)
          debug.log('Highlight', `Hover highlight: ${result.object.type} ${result.object.id}`)
        }
      }
      this.hoverTimeout = null
      this.pendingHoverObject = null
    }, this.config.hoverDelay)
  }

  /**
   * Clear selection highlight
   */
  clearSelection(): void {
    if (this.selectionHighlight) {
      this.highlightGroup.remove(this.selectionHighlight)
      this.disposeObject(this.selectionHighlight)
      this.selectionHighlight = null
    }
  }

  /**
   * Clear hover highlight
   */
  clearHover(): void {
    this.cancelPendingHover()

    if (this.hoverHighlight) {
      this.highlightGroup.remove(this.hoverHighlight)
      this.disposeObject(this.hoverHighlight)
      this.hoverHighlight = null
    }
  }

  /**
   * Cancel pending hover timeout
   */
  private cancelPendingHover(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
      this.hoverTimeout = null
    }
    this.pendingHoverObject = null
  }

  /**
   * Create highlight geometry based on object type
   */
  private createHighlight(result: SelectionResult, color: number, opacity: number): THREE.Object3D | null {
    const { object, mesh } = result
    switch (object.type) {
      case 'plant':
        return this.createPlantHighlight(object as Plant, mesh, color, opacity)
      case 'building':
        return this.createBuildingHighlight(object as Building, mesh, color, opacity)
      case 'rectangle':
      case 'polygon':
      case 'ellipse':
        return this.createGroundHighlight(object as GroundShape, color, opacity)
      case 'zone':
        return this.createZoneHighlight(object as Zone, mesh, color, opacity)
      case 'pin':
        return this.createPinHighlight(object as ContextPin, mesh, color, opacity)
      case 'boundary':
        return this.createBoundaryHighlight(object as BoundaryObject, color, opacity)
      default:
        debug.warn('Highlight', `Unknown object type: ${(object as SelectableObject).type}`)
        return null
    }
  }

  /**
   * Create cylinder highlight for plants
   * Uses actual mesh bounding box for accurate sizing
   */
  private createPlantHighlight(_plant: Plant, mesh: THREE.Object3D, color: number, opacity: number): THREE.Object3D {
    // Get actual bounding box from the mesh
    const box = new THREE.Box3().setFromObject(mesh)
    const size = new THREE.Vector3()
    box.getSize(size)
    const center = new THREE.Vector3()
    box.getCenter(center)

    // Use the larger of X/Z dimensions as radius
    const radius = Math.max(size.x, size.z) / 2
    const cylinderHeight = size.y

    const group = new THREE.Group()

    // Create semi-transparent cylinder surface
    const geometry = new THREE.CylinderGeometry(radius, radius, cylinderHeight, 32, 1, true)
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      side: THREE.DoubleSide,
      depthWrite: false
    })
    const cylinder = new THREE.Mesh(geometry, material)
    group.add(cylinder)

    // Create thick top ring using tube geometry
    const topRing = this.createCircleRing(radius, cylinderHeight / 2, color, opacity)
    group.add(topRing)

    // Create thick bottom ring using tube geometry
    const bottomRing = this.createCircleRing(radius, -cylinderHeight / 2, color, opacity)
    group.add(bottomRing)

    // Create crosshair at bottom for positioning
    const crosshair = this.createCrosshair(radius, -cylinderHeight / 2, color, opacity)
    group.add(crosshair)

    // Position at actual mesh center
    group.position.copy(center)

    return group
  }

  /**
   * Create a thick circular ring at specified Y offset
   */
  private createCircleRing(radius: number, yOffset: number, color: number, opacity: number): THREE.Mesh {
    // Create circle path
    const segments = 32
    const points: THREE.Vector3[] = []
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2
      points.push(new THREE.Vector3(
        Math.cos(angle) * radius,
        yOffset,
        Math.sin(angle) * radius
      ))
    }

    const curve = new THREE.CatmullRomCurve3(points, true)
    const tubeGeometry = new THREE.TubeGeometry(curve, 32, this.config.lineWidth, 8, true)
    const tubeMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: opacity + 0.3,  // Rings slightly more visible than fill
      depthWrite: false
    })

    return new THREE.Mesh(tubeGeometry, tubeMaterial)
  }

  /**
   * Create a crosshair (two perpendicular lines) at specified Y offset
   */
  private createCrosshair(radius: number, yOffset: number, color: number, opacity: number): THREE.Group {
    const group = new THREE.Group()

    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: opacity + 0.3,
      depthWrite: false
    })

    // Line along X axis
    const xLine = new THREE.CatmullRomCurve3([
      new THREE.Vector3(-radius, yOffset, 0),
      new THREE.Vector3(radius, yOffset, 0)
    ])
    const xTube = new THREE.TubeGeometry(xLine, 2, this.config.lineWidth, 8, false)
    group.add(new THREE.Mesh(xTube, material))

    // Line along Z axis
    const zLine = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, yOffset, -radius),
      new THREE.Vector3(0, yOffset, radius)
    ])
    const zTube = new THREE.TubeGeometry(zLine, 2, this.config.lineWidth, 8, false)
    group.add(new THREE.Mesh(zTube, material))

    return group
  }

  /**
   * Create box wireframe highlight for buildings
   * Uses actual mesh bounding box for accurate sizing and positioning
   */
  private createBuildingHighlight(_building: Building, mesh: THREE.Object3D, color: number, opacity: number): THREE.Object3D {
    // To get proper oriented bounding box, compute bounds in local space
    // by temporarily removing rotation
    const originalRotation = mesh.rotation.clone()
    mesh.rotation.set(0, 0, 0)
    mesh.updateMatrixWorld(true)

    const box = new THREE.Box3().setFromObject(mesh)
    const size = new THREE.Vector3()
    box.getSize(size)
    const center = new THREE.Vector3()
    box.getCenter(center)

    // Restore rotation
    mesh.rotation.copy(originalRotation)
    mesh.updateMatrixWorld(true)

    // Create group for the box edges
    const group = new THREE.Group()

    // Half dimensions
    const hx = size.x / 2
    const hy = size.y / 2
    const hz = size.z / 2

    // Define the 12 edges of a box as line segments
    const edges = [
      // Bottom face
      [[-hx, -hy, -hz], [hx, -hy, -hz]],
      [[hx, -hy, -hz], [hx, -hy, hz]],
      [[hx, -hy, hz], [-hx, -hy, hz]],
      [[-hx, -hy, hz], [-hx, -hy, -hz]],
      // Top face
      [[-hx, hy, -hz], [hx, hy, -hz]],
      [[hx, hy, -hz], [hx, hy, hz]],
      [[hx, hy, hz], [-hx, hy, hz]],
      [[-hx, hy, hz], [-hx, hy, -hz]],
      // Vertical edges
      [[-hx, -hy, -hz], [-hx, hy, -hz]],
      [[hx, -hy, -hz], [hx, hy, -hz]],
      [[hx, -hy, hz], [hx, hy, hz]],
      [[-hx, -hy, hz], [-hx, hy, hz]],
    ]

    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: opacity + 0.3,
      depthWrite: false
    })

    // Create tube for each edge
    for (const [start, end] of edges) {
      const curve = new THREE.LineCurve3(
        new THREE.Vector3(start[0], start[1], start[2]),
        new THREE.Vector3(end[0], end[1], end[2])
      )
      const tubeGeometry = new THREE.TubeGeometry(curve, 1, this.config.lineWidth, 8, false)
      group.add(new THREE.Mesh(tubeGeometry, material))
    }

    // Position at mesh center and apply rotation
    group.position.copy(center)
    group.rotation.copy(originalRotation)

    return group
  }

  /**
   * Create outline highlight for ground shapes
   */
  private createGroundHighlight(shape: GroundShape, color: number, opacity: number): THREE.Object3D {
    const group = new THREE.Group()

    // Get shape points in world coordinates
    const points = this.getShapeOutlinePoints(shape)

    if (points.length < 2) {
      debug.warn('Highlight', 'Not enough points for ground highlight')
      return group
    }

    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: opacity + 0.3,
      depthWrite: false
    })

    // Create tube segments for each edge (straight lines, not spline)
    for (let i = 0; i < points.length; i++) {
      const start = points[i]
      const end = points[(i + 1) % points.length]

      const curve = new THREE.LineCurve3(start, end)
      const tubeGeometry = new THREE.TubeGeometry(curve, 1, this.config.lineWidth, 8, false)
      group.add(new THREE.Mesh(tubeGeometry, material))
    }

    return group
  }

  /**
   * Get outline points for a ground shape in world coordinates.
   * Ground shapes use center convention: x,y = CENTER, points = CENTER-RELATIVE
   * (same as ZoneRenderer).
   */
  private getShapeOutlinePoints(shape: GroundShape): THREE.Vector3[] {
    const cx = shape.x * DPM_SCALE
    const cz = shape.y * DPM_SCALE
    const elevation = (shape.elevation || 0) * DPM_SCALE + 0.02 // Slightly above ground
    const rot = shape.rotation * Math.PI / 180
    const cosR = Math.cos(rot)
    const sinR = Math.sin(rot)

    // Rotate a center-relative point (dx, dz) around center
    const rotatePoint = (dx: number, dz: number): THREE.Vector3 => {
      return new THREE.Vector3(
        cx + dx * cosR - dz * sinR,
        elevation,
        cz + dx * sinR + dz * cosR
      )
    }

    switch (shape.type) {
      case 'rectangle': {
        const halfW = (shape.width / 2) * DPM_SCALE
        const halfH = (shape.height / 2) * DPM_SCALE
        return [
          rotatePoint(-halfW, -halfH),
          rotatePoint( halfW, -halfH),
          rotatePoint( halfW,  halfH),
          rotatePoint(-halfW,  halfH)
        ]
      }

      case 'polygon': {
        if (!shape.points || shape.points.length < 3) {
          return this.getShapeOutlinePoints({ ...shape, type: 'rectangle' })
        }
        // Points are center-relative; rotate each around center
        return shape.points.map(p => rotatePoint(p.x * DPM_SCALE, p.y * DPM_SCALE))
      }

      case 'ellipse': {
        const radiusX = (shape.width / 2) * DPM_SCALE
        const radiusZ = (shape.height / 2) * DPM_SCALE
        const segments = 32
        const points: THREE.Vector3[] = []
        for (let i = 0; i < segments; i++) {
          const angle = (i / segments) * Math.PI * 2
          const dx = Math.cos(angle) * radiusX
          const dz = Math.sin(angle) * radiusZ
          points.push(rotatePoint(dx, dz))
        }
        return points
      }

      default:
        return []
    }
  }

  /**
   * Create outline highlight for zones using actual shape geometry.
   * Bottom + top outlines connected by vertical edges for 3D prisms.
   */
  private createZoneHighlight(zone: Zone, _mesh: THREE.Object3D, color: number, opacity: number): THREE.Object3D {
    const group = new THREE.Group()

    // Reuse ground shape outline logic — Zone has the same geometry fields
    const shapeType = zone.points && zone.points.length >= 3
      ? 'polygon'
      : zone.shapeKind === 'ellipse' ? 'ellipse' : 'rectangle'
    const asShape = {
      type: shapeType,
      x: zone.x, y: zone.y,
      width: zone.width, height: zone.height,
      rotation: zone.rotation,
      elevation: 0, // we handle elevation ourselves for top/bottom
      points: zone.points,
    } as GroundShape

    const bottomPoints = this.getShapeOutlinePoints(asShape)
    if (bottomPoints.length < 2) return group

    const prismHeight = zone.elevation * DPM_SCALE
    const topPoints = bottomPoints.map(p =>
      new THREE.Vector3(p.x, p.y + prismHeight, p.z)
    )

    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: opacity + 0.3,
      depthWrite: false
    })

    const addEdge = (start: THREE.Vector3, end: THREE.Vector3) => {
      const curve = new THREE.LineCurve3(start, end)
      const tubeGeometry = new THREE.TubeGeometry(curve, 1, this.config.lineWidth, 8, false)
      group.add(new THREE.Mesh(tubeGeometry, material))
    }

    // Bottom outline
    for (let i = 0; i < bottomPoints.length; i++) {
      addEdge(bottomPoints[i], bottomPoints[(i + 1) % bottomPoints.length])
    }

    // Top outline + vertical edges (only if prism has height)
    if (prismHeight > 0) {
      for (let i = 0; i < topPoints.length; i++) {
        addEdge(topPoints[i], topPoints[(i + 1) % topPoints.length])
        addEdge(bottomPoints[i], topPoints[i])
      }
    }

    return group
  }

  /**
   * Create cylinder highlight for pins
   */
  private createPinHighlight(_pin: ContextPin, mesh: THREE.Object3D, color: number, opacity: number): THREE.Object3D {
    // Get bounding box from mesh
    const box = new THREE.Box3().setFromObject(mesh)
    const size = new THREE.Vector3()
    box.getSize(size)
    const center = new THREE.Vector3()
    box.getCenter(center)

    const radius = Math.max(size.x, size.z) / 2 + 0.1 // Add padding
    const cylinderHeight = size.y + 0.1

    const group = new THREE.Group()

    // Create semi-transparent cylinder surface
    const geometry = new THREE.CylinderGeometry(radius, radius, cylinderHeight, 16, 1, true)
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      side: THREE.DoubleSide,
      depthWrite: false
    })
    const cylinder = new THREE.Mesh(geometry, material)
    group.add(cylinder)

    // Create thick top ring
    const topRing = this.createCircleRing(radius, cylinderHeight / 2, color, opacity)
    group.add(topRing)

    // Create thick bottom ring
    const bottomRing = this.createCircleRing(radius, -cylinderHeight / 2, color, opacity)
    group.add(bottomRing)

    // Position at mesh center
    group.position.copy(center)

    return group
  }

  /**
   * Create outline highlight for boundary (property boundary polygon)
   * Uses absolute coordinates (outline.x + point.x)
   */
  private createBoundaryHighlight(boundary: BoundaryObject, color: number, opacity: number): THREE.Object3D {
    const group = new THREE.Group()

    if (!boundary.points || boundary.points.length < 3) return group

    const Y_HEIGHT = 0.05 // Slightly above boundary tubes

    const points3D = boundary.points.map(p =>
      new THREE.Vector3(
        (boundary.x + p.x) * DPM_SCALE,
        Y_HEIGHT,
        (boundary.y + p.y) * DPM_SCALE
      )
    )

    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: opacity + 0.3,
      depthWrite: false
    })

    for (let i = 0; i < points3D.length; i++) {
      const start = points3D[i]
      const end = points3D[(i + 1) % points3D.length]

      const curve = new THREE.LineCurve3(start, end)
      const tubeGeometry = new THREE.TubeGeometry(curve, 1, this.config.lineWidth, 8, false)
      group.add(new THREE.Mesh(tubeGeometry, material))
    }

    return group
  }

  /**
   * Dispose of a Three.js object and its resources
   */
  private disposeObject(object: THREE.Object3D): void {
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        } else if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose())
        }
      } else if (child instanceof THREE.LineSegments || child instanceof THREE.Line) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        }
      }
    })
  }

  /** Show highlights for multiple selected objects */
  showMultiSelection(results: SelectionResult[]): void {
    this.clearMultiSelection()
    for (const result of results) {
      const highlight = this.createHighlight(result, this.config.selectionColor, this.config.selectionOpacity)
      if (highlight) {
        this.multiSelectionHighlights.push(highlight)
        this.highlightGroup.add(highlight)
      }
    }
  }

  /** Clear all multi-selection highlights */
  clearMultiSelection(): void {
    for (const h of this.multiSelectionHighlights) {
      this.highlightGroup.remove(h)
      this.disposeObject(h)
    }
    this.multiSelectionHighlights = []
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    this.clearSelection()
    this.clearHover()
    this.clearMultiSelection()
    this.scene.remove(this.highlightGroup)
  }
}
